import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AcademicEvent, EventCategory } from '../../services/annualPlanningService';
import { calendarSettingsService, AcademicYearTemplate, RamadanDates } from '../../services/calendarSettingsService';
import { PublicHoliday } from '../../services/holidaysService';

interface AcademicYearMonthlyViewProps {
  academicYearStart: number;
  events: AcademicEvent[];
  categories: EventCategory[];
  holidays: PublicHoliday[];
  selectedCategoryIds?: string[]; // For filtering - if empty, show all
  onEventClick: (event: AcademicEvent) => void;
  onDateClick?: (date: Date) => void;
  onDateRangeSelect?: (startDate: Date, endDate: Date) => void;
  onHolidayClick?: (holiday: PublicHoliday) => void;
  initialDate?: Date;
  isEditMode?: boolean;
}

interface DragState {
  isSelecting: boolean;
  startDate: Date | null;
  endDate: Date | null;
}

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export const AcademicYearMonthlyView: React.FC<AcademicYearMonthlyViewProps> = ({
  academicYearStart,
  events,
  categories,
  holidays,
  selectedCategoryIds,
  onEventClick,
  onDateClick,
  onDateRangeSelect,
  onHolidayClick,
  initialDate,
  isEditMode = false,
}) => {
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [template, setTemplate] = useState<AcademicYearTemplate | null>(null);
  const [ramadanDates, setRamadanDates] = useState<RamadanDates | null>(null);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Drag selection state for multi-day events
  const [dragState, setDragState] = useState<DragState>({
    isSelecting: false,
    startDate: null,
    endDate: null,
  });

  // Check if user can add events (has onDateClick or onDateRangeSelect)
  const canAddEvents = !!(onDateClick || onDateRangeSelect);

  // Handle mouse up globally to complete drag selection
  React.useEffect(() => {
    if (!dragState.isSelecting) {
      return;
    }

    const handleMouseUp = () => {
      if (dragState.startDate && dragState.endDate) {
        const start = dragState.startDate < dragState.endDate ? dragState.startDate : dragState.endDate;
        const end = dragState.startDate < dragState.endDate ? dragState.endDate : dragState.startDate;

        if (start.getTime() === end.getTime()) {
          // Single day click
          onDateClick?.(start);
        } else {
          // Multi-day drag
          onDateRangeSelect?.(start, end);
        }
      }
      setDragState({
        isSelecting: false,
        startDate: null,
        endDate: null,
      });
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dragState, onDateClick, onDateRangeSelect]);

  // Helper to check if a date is in the selection range
  const isDateInSelection = (day: number): boolean => {
    if (!dragState.isSelecting || !dragState.startDate || !dragState.endDate) return false;

    const date = new Date(year, month, day);
    const start = dragState.startDate < dragState.endDate ? dragState.startDate : dragState.endDate;
    const end = dragState.startDate < dragState.endDate ? dragState.endDate : dragState.startDate;

    return date >= start && date <= end;
  };

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const data = await calendarSettingsService.getAcademicYearTemplate();
        setTemplate(data);
      } catch (error) {
        console.error('Failed to load academic year template:', error);
      }
    };
    loadTemplate();
  }, []);

  useEffect(() => {
    const loadRamadan = async () => {
      try {
        const ramadan1 = await calendarSettingsService.getRamadanDatesForYear(academicYearStart);
        const ramadan2 = await calendarSettingsService.getRamadanDatesForYear(academicYearStart + 1);
        setRamadanDates(ramadan2 || ramadan1);
      } catch (error) {
        console.error('Failed to load Ramadan dates:', error);
      }
    };
    loadRamadan();
  }, [academicYearStart]);

  const academicStartMonth = useMemo(() => {
    return template?.startMonth ? template.startMonth - 1 : 8;
  }, [template]);

  const academicYearBoundaries = useMemo(() => {
    if (!template) return null;

    const startYear = academicYearStart;
    const endYear = academicYearStart + 1;

    const getYearForMonth = (monthIndex: number): number => {
      return monthIndex < academicStartMonth ? endYear : startYear;
    };

    const startMonth = template.startMonth - 1;
    const endMonth = template.endMonth - 1;

    return {
      start: new Date(getYearForMonth(startMonth), startMonth, template.startDay || 1),
      end: new Date(getYearForMonth(endMonth), endMonth, template.endDay || 31),
      sem1Start: template.semester1StartMonth ? new Date(
        getYearForMonth(template.semester1StartMonth - 1),
        template.semester1StartMonth - 1,
        template.semester1StartDay || 1
      ) : null,
      sem1End: template.semester1EndMonth ? new Date(
        getYearForMonth(template.semester1EndMonth - 1),
        template.semester1EndMonth - 1,
        template.semester1EndDay || 31
      ) : null,
      sem2Start: template.semester2StartMonth ? new Date(
        getYearForMonth(template.semester2StartMonth - 1),
        template.semester2StartMonth - 1,
        template.semester2StartDay || 1
      ) : null,
      sem2End: template.semester2EndMonth ? new Date(
        getYearForMonth(template.semester2EndMonth - 1),
        template.semester2EndMonth - 1,
        template.semester2EndDay || 31
      ) : null,
    };
  }, [template, academicYearStart, academicStartMonth]);

  // Check if a category should be shown based on selected filters
  const shouldShowCategory = (categoryCode: string): boolean => {
    // If no filter is active, show everything
    if (!selectedCategoryIds?.length) return true;
    // Find the category by code and check if it's selected
    const category = categories.find(c => c.code === categoryCode);
    return category ? selectedCategoryIds.includes(category.id) : false;
  };

  // Academic markers for the current month - only show if ACADEMIC category is selected or no filter
  const academicMarkersForMonth = useMemo(() => {
    if (!academicYearBoundaries) return [];
    // Only show markers if ACADEMIC category is selected or no filter is active
    if (!shouldShowCategory('ACADEMIC')) return [];

    // Use ACADEMIC category color for all academic markers
    const academicCategory = categories.find(c => c.code === 'ACADEMIC');
    const academicColor = academicCategory?.color || '#3B82F6'; // Default to blue if no ACADEMIC category

    const markers: { day: number; label: string; color: string; type: string }[] = [];

    // Check if year start is in this month
    if (academicYearBoundaries.start.getMonth() === month && academicYearBoundaries.start.getFullYear() === year) {
      markers.push({ day: academicYearBoundaries.start.getDate(), label: "Début Année", color: academicColor, type: 'year-start' });
    }

    // Check if year end is in this month
    if (academicYearBoundaries.end.getMonth() === month && academicYearBoundaries.end.getFullYear() === year) {
      markers.push({ day: academicYearBoundaries.end.getDate(), label: "Fin Année", color: academicColor, type: 'year-end' });
    }

    // Semester 1 start
    if (academicYearBoundaries.sem1Start && academicYearBoundaries.sem1Start.getMonth() === month && academicYearBoundaries.sem1Start.getFullYear() === year) {
      markers.push({ day: academicYearBoundaries.sem1Start.getDate(), label: "Début S1", color: academicColor, type: 'sem1-start' });
    }

    // Semester 1 end
    if (academicYearBoundaries.sem1End && academicYearBoundaries.sem1End.getMonth() === month && academicYearBoundaries.sem1End.getFullYear() === year) {
      markers.push({ day: academicYearBoundaries.sem1End.getDate(), label: "Fin S1", color: academicColor, type: 'sem1-end' });
    }

    // Semester 2 start
    if (academicYearBoundaries.sem2Start && academicYearBoundaries.sem2Start.getMonth() === month && academicYearBoundaries.sem2Start.getFullYear() === year) {
      markers.push({ day: academicYearBoundaries.sem2Start.getDate(), label: "Début S2", color: academicColor, type: 'sem2-start' });
    }

    // Semester 2 end
    if (academicYearBoundaries.sem2End && academicYearBoundaries.sem2End.getMonth() === month && academicYearBoundaries.sem2End.getFullYear() === year) {
      markers.push({ day: academicYearBoundaries.sem2End.getDate(), label: "Fin S2", color: academicColor, type: 'sem2-end' });
    }

    return markers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYearBoundaries, month, year, categories, selectedCategoryIds]);

  // Get academic markers for a specific day
  const getMarkersForDay = (day: number) => {
    return academicMarkersForMonth.filter(m => m.day === day);
  };

  const isRamadanDate = (checkYear: number, checkMonth: number, day: number): boolean => {
    if (!ramadanDates) return false;
    const date = new Date(checkYear, checkMonth, day);
    const ramadanStart = new Date(ramadanDates.startDate);
    const ramadanEnd = new Date(ramadanDates.endDate);
    return date >= ramadanStart && date <= ramadanEnd;
  };

  // Check if entire month is during Ramadan
  const isRamadanMonth = useMemo(() => {
    if (!ramadanDates) return false;
    const ramadanStart = new Date(ramadanDates.startDate);
    const ramadanEnd = new Date(ramadanDates.endDate);
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    // Check if any part of the month overlaps with Ramadan
    return monthStart <= ramadanEnd && monthEnd >= ramadanStart;
  }, [ramadanDates, year, month]);

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

  const eventsByDate = useMemo(() => {
    const map = new Map<string, AcademicEvent[]>();

    events.forEach(event => {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);

      const current = new Date(start);
      while (current <= end) {
        const key = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(event);
        current.setDate(current.getDate() + 1);
      }
    });

    return map;
  }, [events]);

  const holidaysByDate = useMemo(() => {
    const map = new Map<string, PublicHoliday[]>();
    const seenHolidays = new Set<string>();

    holidays.forEach(holiday => {
      const holidayKey = `${holiday.name}-${holiday.startDate}`;
      if (seenHolidays.has(holidayKey)) return;
      seenHolidays.add(holidayKey);

      const start = new Date(holiday.startDate);
      const end = new Date(holiday.endDate);

      const current = new Date(start);
      while (current <= end) {
        const key = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`;
        if (!map.has(key)) {
          map.set(key, []);
        }
        const existing = map.get(key)!;
        if (!existing.some(h => h.name === holiday.name)) {
          existing.push(holiday);
        }
        current.setDate(current.getDate() + 1);
      }
    });

    return map;
  }, [holidays]);

  const currentSemester = useMemo(() => {
    if (!academicYearBoundaries) return null;

    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);

    if (academicYearBoundaries.sem1Start && academicYearBoundaries.sem1End) {
      if (monthStart >= academicYearBoundaries.sem1Start && monthEnd <= academicYearBoundaries.sem1End) {
        return 'sem1';
      }
    }

    if (academicYearBoundaries.sem2Start && academicYearBoundaries.sem2End) {
      if (monthStart >= academicYearBoundaries.sem2Start && monthEnd <= academicYearBoundaries.sem2End) {
        return 'sem2';
      }
    }

    return null;
  }, [year, month, academicYearBoundaries]);

  const isInAcademicYear = useMemo(() => {
    if (!academicYearBoundaries) return true;

    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);

    return monthStart <= academicYearBoundaries.end && monthEnd >= academicYearBoundaries.start;
  }, [year, month, academicYearBoundaries]);

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getEventsForDate = (day: number): AcademicEvent[] => {
    const key = `${year}-${month}-${day}`;
    return eventsByDate.get(key) || [];
  };

  const getHolidaysForDate = (day: number): PublicHoliday[] => {
    const key = `${year}-${month}-${day}`;
    return holidaysByDate.get(key) || [];
  };

  const getEventColor = (event: AcademicEvent): string => {
    if (event.color) return event.color;
    if (event.category?.color) return event.category.color;
    const category = categories.find(c => c.id === event.categoryId);
    return category?.color || '#6B7280';
  };

  const isToday = (day: number): boolean => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === month &&
      today.getFullYear() === year
    );
  };

  const calendarDays: (number | null)[] = [];

  for (let i = 0; i < adjustedStartingDay; i++) {
    calendarDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePreviousMonth}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <h2 className="text-base font-medium text-gray-900 ml-2">
            {MONTH_NAMES[month]} {year}
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {currentSemester && (
            <span className={`text-xs px-2 py-1 rounded font-medium ${
              currentSemester === 'sem1' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'
            }`}>
              {currentSemester === 'sem1' ? 'Semestre 1' : 'Semestre 2'}
            </span>
          )}

          {isRamadanMonth && (
            <span className="text-xs px-2 py-1 rounded font-medium bg-emerald-100 text-emerald-700">
              Mois de Ramadan
            </span>
          )}

          {!isInAcademicYear && (
            <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">
              Hors année académique
            </span>
          )}

          <button
            onClick={handleToday}
            className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            Aujourd'hui
          </button>
        </div>
      </div>

      {/* Year indicator */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="font-medium text-gray-700">
          {academicYearStart}-{academicYearStart + 1}
        </span>
      </div>

      {/* Calendar Grid */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* Week day headers - all same color */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {WEEK_DAYS.map((day) => (
            <div
              key={day}
              className="px-2 py-2 text-center text-xs font-medium text-gray-600 uppercase"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            if (day === null) {
              return (
                <div
                  key={`empty-${index}`}
                  className="min-h-[100px] p-2 border-b border-r border-gray-100 bg-gray-50/50"
                />
              );
            }

            const dayEvents = getEventsForDate(day);
            const dayHolidays = getHolidaysForDate(day);
            const dayMarkers = getMarkersForDay(day);
            const today = isToday(day);
            const hasHoliday = dayHolidays.length > 0;
            const hasMarkers = dayMarkers.length > 0;
            const isRamadan = isRamadanDate(year, month, day);
            const hasContent = dayEvents.length > 0 || hasHoliday || isRamadan || hasMarkers;
            const isHovered = hoveredDay === day;
            const inSelection = isDateInSelection(day);

            // Subtle background for Ramadan days only
            // Get VACATION category color for holiday background
            const vacationCategory = categories.find(c => c.code === 'VACATION');
            const vacationColor = vacationCategory?.color || '#22C55E';
            let cellBg = 'bg-white';
            let cellStyle: React.CSSProperties = {};
            if (inSelection) {
              cellBg = 'bg-primary-200';
            } else if (hasHoliday) {
              // Use VACATION category color with low opacity for background
              cellStyle = { backgroundColor: `${vacationColor}15` }; // 15 = ~8% opacity in hex
            } else if (isRamadan) {
              cellBg = 'bg-emerald-100';
            }

            return (
              <div
                key={day}
                className={`
                  min-h-[100px] p-2 border-b border-r border-gray-100 relative select-none
                  ${cellBg}
                  ${canAddEvents ? 'cursor-pointer hover:bg-primary-100' : hasContent ? 'cursor-pointer' : ''}
                  transition-colors
                `}
                style={inSelection ? {} : cellStyle}
                onMouseEnter={() => {
                  setHoveredDay(day);
                  if (dragState.isSelecting) {
                    setDragState(prev => ({
                      ...prev,
                      endDate: new Date(year, month, day),
                    }));
                  }
                }}
                onMouseLeave={() => setHoveredDay(null)}
                onMouseDown={(e) => {
                  if (canAddEvents) {
                    e.preventDefault();
                    const clickedDate = new Date(year, month, day);
                    setDragState({
                      isSelecting: true,
                      startDate: clickedDate,
                      endDate: clickedDate,
                    });
                  }
                }}
              >
                {/* Date number */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`
                      w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium
                      ${today ? 'bg-blue-500 text-white' : 'text-gray-700'}
                    `}
                  >
                    {day}
                  </span>
                </div>

                {/* Events display */}
                <div className="space-y-0.5">
                  {/* Academic markers first */}
                  {getMarkersForDay(day).map((marker) => (
                    <div
                      key={marker.type}
                      className="w-full text-left text-[10px] px-1.5 py-0.5 rounded text-white truncate font-medium"
                      style={{ backgroundColor: marker.color }}
                    >
                      {marker.label}
                    </div>
                  ))}
                  {dayHolidays.slice(0, 2 - getMarkersForDay(day).length).map((holiday) => (
                    <button
                      key={holiday.id}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onHolidayClick?.(holiday);
                      }}
                      className="w-full text-left text-[10px] px-1.5 py-0.5 rounded text-white hover:opacity-90 transition-colors truncate font-medium"
                      style={{ backgroundColor: vacationColor }}
                    >
                      {holiday.name}
                    </button>
                  ))}
                  {dayEvents.slice(0, 3 - dayHolidays.length - getMarkersForDay(day).length).map((event) => (
                    <button
                      key={event.id}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                      className="w-full text-left text-[10px] px-1.5 py-0.5 rounded text-white hover:opacity-90 transition-opacity truncate font-medium"
                      style={{ backgroundColor: getEventColor(event) }}
                    >
                      {event.title}
                    </button>
                  ))}
                  {(dayEvents.length + dayHolidays.length + getMarkersForDay(day).length) > 3 && (
                    <span className="text-[10px] text-gray-500 px-1">
                      +{(dayEvents.length + dayHolidays.length + getMarkersForDay(day).length) - 3}
                    </span>
                  )}
                </div>

                {/* Hover tooltip for empty days with Ramadan */}
                {isHovered && isRamadan && !hasHoliday && dayEvents.length === 0 && !isEditMode && (
                  <div className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-1 bg-emerald-600 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
                    Mois de Ramadan
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-emerald-600" />
                  </div>
                )}

                {/* Hover tooltip showing all events - position based on row */}
                {isHovered && hasContent && (dayEvents.length > 0 || hasHoliday || hasMarkers) && !isEditMode && (
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`absolute z-50 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px] ${
                      index < 14 ? 'top-full mt-1' : 'bottom-full mb-1'
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-900 mb-2 border-b border-gray-100 pb-2">
                      {day} {MONTH_NAMES[month]} {year}
                    </div>
                    {isRamadan && (
                      <div className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 mb-2 inline-block font-medium">
                        Mois de Ramadan
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {/* Academic markers */}
                      {dayMarkers.map((marker) => (
                        <div
                          key={marker.type}
                          className="text-xs px-2 py-1.5 rounded text-white font-medium"
                          style={{ backgroundColor: marker.color }}
                        >
                          {marker.label}
                        </div>
                      ))}
                      {dayHolidays.map((holiday, idx) => {
                        const vacationCat = categories.find(c => c.code === 'VACATION');
                        const vacColor = vacationCat?.color || '#22C55E';
                        return (
                          <div
                            key={`h-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onHolidayClick?.(holiday);
                            }}
                            className="text-xs px-2 py-1.5 rounded cursor-pointer hover:opacity-80 font-medium"
                            style={{ backgroundColor: `${vacColor}20`, color: vacColor }}
                          >
                            {holiday.name}
                          </div>
                        );
                      })}
                      {dayEvents.map((event, idx) => (
                        <div
                          key={`e-${idx}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer"
                        >
                          <div
                            className="w-3 h-3 rounded flex-shrink-0"
                            style={{ backgroundColor: getEventColor(event) }}
                          />
                          <span className="text-gray-800 font-medium">{event.title}</span>
                        </div>
                      ))}
                    </div>
                    <div className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent ${
                      index < 14
                        ? 'bottom-full border-b-[6px] border-b-white'
                        : 'top-full border-t-[6px] border-t-white'
                    }`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend - All categories including Période de Vacances and Ramadan */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: categories.find(c => c.code === 'VACATION')?.color || '#22C55E' }}
          />
          <span className="text-gray-600">Période de Vacances</span>
        </div>
        {categories.filter(c => c.isActive && c.code !== 'VACATION').map(category => (
          <div key={category.id} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: category.color }}
            />
            <span className="text-gray-600">{category.name}</span>
          </div>
        ))}
        {ramadanDates && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-300" />
            <span className="text-gray-600">Ramadan</span>
          </div>
        )}
      </div>

      {/* Academic Markers Legend */}
      {academicMarkersForMonth.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs mt-2 pt-2 border-t border-gray-100">
          <span className="text-gray-500 font-medium">Ce mois:</span>
          {academicMarkersForMonth.map(marker => (
            <div key={marker.type} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: marker.color }}
              />
              <span className="text-gray-600">{marker.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
